import type { SyncDoc } from "./types.ts";
import { emptyDoc } from "./types.ts";

/**
 * Merges two study documents.
 *
 * Every entry carries its own `updatedAt`, so the merge is per question rather
 * than per document: answering different questions on two devices keeps both
 * results, and answering the same question twice keeps the later one. The
 * operation is commutative, so it does not matter which side runs it.
 */
export function mergeDocs(a: SyncDoc, b: SyncDoc): SyncDoc {
  const out = emptyDoc();

  for (const key of union(a.progress, b.progress)) {
    const left = a.progress[key];
    const right = b.progress[key];
    const winner =
      !left ? right : !right ? left : right.updatedAt > left.updatedAt ? right : left;
    if (winner) out.progress[key] = winner;
  }

  for (const key of union(a.marked, b.marked)) {
    const left = a.marked[key];
    const right = b.marked[key];
    const winner =
      !left ? right : !right ? left : right.updatedAt > left.updatedAt ? right : left;
    if (winner) out.marked[key] = winner;
  }

  out.updatedAt = Math.max(a.updatedAt, b.updatedAt);
  return out;
}

function union(a: Record<string, unknown>, b: Record<string, unknown>): string[] {
  return [...new Set([...Object.keys(a), ...Object.keys(b)])];
}

/** Rejects anything that is not a well-formed document before storing it. */
export function isSyncDoc(value: unknown): value is SyncDoc {
  if (!value || typeof value !== "object") return false;
  const doc = value as Partial<SyncDoc>;
  return (
    typeof doc.updatedAt === "number" &&
    !!doc.progress && typeof doc.progress === "object" &&
    !!doc.marked && typeof doc.marked === "object"
  );
}
